// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, orderBy, updateDoc, doc, increment, deleteDoc, where, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import PostCatchModal from '../components/PostCatchModal';
import { Users, Plus, Fish, Heart, MessageCircle, Calendar, MapPin, Ruler, Search, UserPlus, UserMinus, MoreVertical, Pencil, Trash2 } from 'lucide-react';

const Community = ({ setCurrentPage, openUserProfile, openChatWithUser }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [followingIds, setFollowingIds] = useState(new Set());
  const [discoverUsers, setDiscoverUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [followLoading, setFollowLoading] = useState(null);
  const [expandedCommentsPostId, setExpandedCommentsPostId] = useState(null);
  const [commentsByPostId, setCommentsByPostId] = useState({});
  const [commentText, setCommentText] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(null);
  const [commentSubmitLoading, setCommentSubmitLoading] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [commentMenuOpen, setCommentMenuOpen] = useState(null);
  const { currentUser, userProfile, loadUserProfile } = useAuth();

  useEffect(() => {
    loadPosts();
    loadLikes();
    loadFollowing();
  }, [currentUser]);

  const loadFollowing = async () => {
    if (!currentUser) return;
    try {
      const q = query(
        collection(db, 'follows'),
        where('followerId', '==', currentUser.uid)
      );
      const snapshot = await getDocs(q);
      const ids = new Set(snapshot.docs.map(d => d.data().followingId));
      setFollowingIds(ids);
    } catch (error) {
      console.error('Error loading following:', error);
    }
  };

  const searchUsers = async () => {
    if (!currentUser || !userSearchQuery.trim()) return;
    setUserSearchLoading(true);
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const query = userSearchQuery.trim().toLowerCase();
      const list = usersSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.uid !== currentUser.uid)
        .filter(u => {
          const name = (u.displayName || '').toLowerCase();
          const email = (u.email || '').toLowerCase();
          return name.includes(query) || email.includes(query);
        })
        .slice(0, 30);
      setDiscoverUsers(list);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const followUser = async (targetUserId) => {
    if (!currentUser || targetUserId === currentUser.uid) return;
    setFollowLoading(targetUserId);
    try {
      await addDoc(collection(db, 'follows'), {
        followerId: currentUser.uid,
        followingId: targetUserId,
        createdAt: serverTimestamp()
      });
      setFollowingIds(prev => new Set([...prev, targetUserId]));
      await loadUserProfile(currentUser.uid);
    } catch (error) {
      console.error('Error following user:', error);
    } finally {
      setFollowLoading(null);
    }
  };

  const unfollowUser = async (targetUserId) => {
    if (!currentUser) return;
    setFollowLoading(targetUserId);
    try {
      const q = query(
        collection(db, 'follows'),
        where('followerId', '==', currentUser.uid),
        where('followingId', '==', targetUserId)
      );
      const snapshot = await getDocs(q);
      for (const d of snapshot.docs) {
        await deleteDoc(doc(db, 'follows', d.id));
      }
      setFollowingIds(prev => {
        const next = new Set(prev);
        next.delete(targetUserId);
        return next;
      });
      await loadUserProfile(currentUser.uid);
    } catch (error) {
      console.error('Error unfollowing user:', error);
    } finally {
      setFollowLoading(null);
    }
  };

  const loadPosts = async () => {
    try {
      const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const postsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLikes = async () => {
    if (!currentUser) return;

    try {
      const q = query(
        collection(db, 'likes'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);
      const liked = new Set(querySnapshot.docs.map(doc => doc.data().postId));
      setLikedPosts(liked);
    } catch (error) {
      console.error('Error loading likes:', error);
    }
  };

  const handleCreatePost = async (postData) => {
    if (!currentUser) {
      throw new Error('Please sign in to post your catch.');
    }

    let imageUrl = null;

    try {
      // Upload image if present
      if (postData.imageFile) {
        const timestamp = Date.now();
        const sanitizedName = (postData.imageFile.name || 'image').replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `catches/${currentUser.uid}/${timestamp}_${sanitizedName}`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, postData.imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error?.code === 'storage/unauthorized' || error?.code === 'storage/retry-limit-exceeded') {
        throw new Error(
          'Image upload failed. Make sure you’re signed in and that Storage rules are deployed. ' +
          'From the project folder run: firebase deploy --only storage'
        );
      }
      throw new Error(error?.message || 'Image upload failed. Please try again or post without a photo.');
    }

    try {
      const newPostPayload = {
        userId: currentUser.uid,
        userName: userProfile?.displayName || currentUser.displayName || 'Anonymous Angler',
        content: postData.content,
        species: postData.species,
        length: postData.length,
        weight: postData.weight,
        location: postData.location,
        dateCaught: postData.dateCaught,
        imageUrl,
        likes: 0,
        comments: 0,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'posts'), newPostPayload);
      const newPostForState = { id: docRef.id, ...newPostPayload, createdAt: new Date() };
      setPosts([newPostForState, ...posts]);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error creating post:', error);
      if (error?.code?.startsWith('firestore/') || error?.message?.includes('Firestore')) {
        throw new Error('Could not save post. Check that Firestore rules allow writes to the posts collection.');
      }
      throw new Error(error?.message || 'Failed to create post. Please try again.');
    }
  };

  const loadComments = async (postId) => {
    if (!postId) return;
    setCommentsLoading(postId);
    try {
      const commentsRef = collection(db, 'posts', postId, 'comments');
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setCommentsByPostId(prev => ({ ...prev, [postId]: list }));
    } catch (error) {
      console.error('Error loading comments:', error);
      setCommentsByPostId(prev => ({ ...prev, [postId]: [] }));
    } finally {
      setCommentsLoading(null);
    }
  };

  const toggleComments = (postId) => {
    const next = expandedCommentsPostId === postId ? null : postId;
    setExpandedCommentsPostId(next);
    setCommentText('');
    if (next && !commentsByPostId[next]) loadComments(next);
  };

  const submitComment = async (postId) => {
    if (!currentUser || !commentText.trim() || commentSubmitLoading) return;
    setCommentSubmitLoading(true);
    try {
      const commentsRef = collection(db, 'posts', postId, 'comments');
      const newComment = {
        userId: currentUser.uid,
        userName: userProfile?.displayName || currentUser.displayName || 'Anonymous Angler',
        text: commentText.trim(),
        createdAt: serverTimestamp()
      };
      const docRef = await addDoc(commentsRef, newComment);
      await updateDoc(doc(db, 'posts', postId), { comments: increment(1) });
      const added = {
        id: docRef.id,
        ...newComment,
        createdAt: new Date()
      };
      setCommentsByPostId(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), added]
      }));
      setPosts(posts.map(p =>
        p.id === postId ? { ...p, comments: (p.comments || 0) + 1 } : p
      ));
      setCommentText('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setCommentSubmitLoading(false);
    }
  };

  const handleEditComment = async (postId, commentId, newText) => {
    if (!currentUser || !newText.trim()) return;
    try {
      const commentRef = doc(db, 'posts', postId, 'comments', commentId);
      await updateDoc(commentRef, { text: newText.trim() });
      setCommentsByPostId(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).map(c =>
          c.id === commentId ? { ...c, text: newText.trim() } : c
        )
      }));
      setEditingComment(null);
      setEditCommentText('');
      setCommentMenuOpen(null);
    } catch (error) {
      console.error('Error editing comment:', error);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!currentUser) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      const commentRef = doc(db, 'posts', postId, 'comments', commentId);
      await deleteDoc(commentRef);
      await updateDoc(doc(db, 'posts', postId), { comments: increment(-1) });
      setCommentsByPostId(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).filter(c => c.id !== commentId)
      }));
      setPosts(posts.map(p =>
        p.id === postId ? { ...p, comments: Math.max(0, (p.comments || 0) - 1) } : p
      ));
      setEditingComment(null);
      setEditCommentText('');
      setCommentMenuOpen(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const startEditComment = (postId, comment) => {
    setEditingComment({ postId, commentId: comment.id });
    setEditCommentText(comment.text || '');
    setCommentMenuOpen(null);
  };

  const cancelEditComment = () => {
    setEditingComment(null);
    setEditCommentText('');
    setCommentMenuOpen(null);
  };

  const toggleLike = async (postId) => {
    if (!currentUser) return;

    try {
      if (likedPosts.has(postId)) {
        // Unlike
        const q = query(
          collection(db, 'likes'),
          where('userId', '==', currentUser.uid),
          where('postId', '==', postId)
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.docs.forEach(async (document) => {
          await deleteDoc(doc(db, 'likes', document.id));
        });

        const postRef = doc(db, 'posts', postId);
        await updateDoc(postRef, {
          likes: increment(-1)
        });

        setLikedPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });

        setPosts(posts.map(p => 
          p.id === postId ? { ...p, likes: p.likes - 1 } : p
        ));
      } else {
        // Like
        await addDoc(collection(db, 'likes'), {
          userId: currentUser.uid,
          postId: postId,
          createdAt: new Date()
        });

        const postRef = doc(db, 'posts', postId);
        await updateDoc(postRef, {
          likes: increment(1)
        });

        setLikedPosts(prev => new Set([...prev, postId]));

        setPosts(posts.map(p => 
          p.id === postId ? { ...p, likes: p.likes + 1 } : p
        ));
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCatchDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h2 className="page-header-title">
            <Users size={32} />
            Community Feed
          </h2>
          <p className="page-header-subtitle">Share your catches with fellow anglers</p>
        </div>
        <div className="empty-state">
          <div style={{ fontSize: '3rem' }}>⏳</div>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="community-page">
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <h2 className="page-header-title">
            <Users size={32} />
            Community Feed
          </h2>
          <p className="page-header-subtitle">Share your catches with fellow anglers</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setIsModalOpen(true)}>
            <Plus size={18} />
            Post Catch
          </button>
        </div>
      </div>

      <div className="section community-content" style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Tabs */}
        <div className="profile-tabs community-tabs" style={{ marginBottom: '2rem' }}>
          <button
            className={`profile-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Posts
          </button>
          <button
            className={`profile-tab ${activeTab === 'following' ? 'active' : ''}`}
            onClick={() => setActiveTab('following')}
          >
            Following
          </button>
          <button
            className={`profile-tab ${activeTab === 'discover' ? 'active' : ''}`}
            onClick={() => setActiveTab('discover')}
          >
            Discover Users
          </button>
        </div>

        {/* Discover Users tab */}
        {activeTab === 'discover' && (
          <div className="section community-discover" style={{ marginBottom: '2rem' }}>
            <div className="community-discover-search" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                    border: '1px solid var(--border-gray)',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <button className="btn btn-primary" onClick={searchUsers} disabled={userSearchLoading || !userSearchQuery.trim()}>
                {userSearchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
            {discoverUsers.length > 0 ? (
              <div className="community-discover-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {discoverUsers.map(user => (
                  <div
                    key={user.uid}
                    className="community-discover-user-card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1rem 1.25rem',
                      background: 'var(--bg-gray-50)',
                      borderRadius: '12px',
                      border: '1px solid var(--border-gray)'
                    }}
                  >
                    <button
                      type="button"
                      className="community-discover-user-card-info"
                      onClick={() => openUserProfile && openUserProfile(user.id || user.uid)}
                    >
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        background: user.photoURL ? 'transparent' : 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1rem',
                        overflow: 'hidden'
                      }}>
                        {user.photoURL ? (
                          <img src={user.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          (user.displayName || user.email || '?').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                          {user.displayName || 'Angler'}
                        </div>
                      </div>
                    </button>
                    <button
                      className={followingIds.has(user.uid) ? 'btn btn-secondary community-discover-follow-btn' : 'btn btn-primary community-discover-follow-btn'}
                      onClick={(e) => {
                        e.stopPropagation();
                        followingIds.has(user.uid) ? unfollowUser(user.uid) : followUser(user.uid);
                      }}
                      disabled={followLoading === user.uid}
                      style={{ minWidth: '100px' }}
                    >
                      {followLoading === user.uid ? '...' : followingIds.has(user.uid) ? (
                        <><UserMinus size={16} /> Following</>
                      ) : (
                        <><UserPlus size={16} /> Follow</>
                      )}
                    </button>
                    {openChatWithUser && (
                      <button
                        type="button"
                        className="btn btn-secondary community-discover-follow-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openChatWithUser(user.uid || user.id);
                        }}
                        style={{ minWidth: '100px' }}
                        title="Message"
                      >
                        <MessageCircle size={16} />
                        Message
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : userSearchQuery.trim() && !userSearchLoading ? (
              <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: '2rem' }}>
                No users found. Try a different search.
              </p>
            ) : (
              <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: '2rem' }}>
                Search by display name or email to find and follow other anglers.
              </p>
            )}
          </div>
        )}

        {activeTab !== 'discover' && (() => {
          const filteredPosts = activeTab === 'following'
            ? posts.filter(p => followingIds.has(p.userId))
            : posts;
          return filteredPosts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <Fish size={60} strokeWidth={1.5} />
              </div>
              <h3 className="empty-title">
                {activeTab === 'following' ? 'No posts from people you follow' : 'No catches yet'}
              </h3>
              <p className="empty-description">
                {activeTab === 'following'
                  ? 'Follow anglers from the Discover Users tab to see their posts here.'
                  : 'Be the first to share your catch!'}
              </p>
              {activeTab !== 'following' && (
                <button className="btn btn-primary" onClick={() => setIsModalOpen(true)}>
                  <Plus size={18} />
                  Post Your Catch
                </button>
              )}
              {activeTab === 'following' && (
                <button className="btn btn-primary" onClick={() => setActiveTab('discover')}>
                  <Search size={18} />
                  Discover Users
                </button>
              )}
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {filteredPosts.map(post => (
              <div key={post.id} className="post-card-container">
                {/* Post Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', padding: '0 1.5rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}>
                    {post.userName?.charAt(0) || 'A'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                      {post.userName || 'Anonymous Angler'}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      {formatDate(post.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Post Image */}
                {post.imageUrl && (
                  <div className="post-image-container">
                    <img src={post.imageUrl} alt="Catch" className="post-image" />
                  </div>
                )}

                <div style={{ padding: '0 1.5rem 1.5rem' }}>
                  {/* Post Content */}
                  <p className="feature-description" style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: 1.6 }}>
                    {post.content}
                  </p>

                  {/* Catch Details */}
                  {(post.species || post.length || post.weight || post.location || post.dateCaught) && (
                    <div style={{
                      background: 'var(--bg-gray-50)',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '0.75rem'
                    }}>
                      {post.species && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <Fish size={16} color="var(--primary-green)" />
                          <span><strong>Species:</strong> {post.species}</span>
                        </div>
                      )}
                      {post.length && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <Ruler size={16} color="var(--primary-green)" />
                          <span><strong>Length:</strong> {post.length}</span>
                        </div>
                      )}
                      {post.weight && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <span>⚖️</span>
                          <span><strong>Weight:</strong> {post.weight}</span>
                        </div>
                      )}
                      {post.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <MapPin size={16} color="var(--primary-green)" />
                          <span><strong>Location:</strong> {post.location}</span>
                        </div>
                      )}
                      {post.dateCaught && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                          <Calendar size={16} color="var(--primary-green)" />
                          <span><strong>Caught:</strong> {formatCatchDate(post.dateCaught)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '1.5rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid var(--border-gray)'
                  }}>
                    <button
                      onClick={() => toggleLike(post.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: likedPosts.has(post.id) ? '#EF4444' : 'var(--text-medium)',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        transition: 'all 0.2s'
                      }}
                    >
                      <Heart size={18} fill={likedPosts.has(post.id) ? 'currentColor' : 'none'} />
                      <span>{post.likes || 0}</span>
                    </button>
                    <button
                      onClick={() => toggleComments(post.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: expandedCommentsPostId === post.id ? 'var(--primary-green)' : 'var(--text-medium)',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        transition: 'color 0.2s'
                      }}
                    >
                      <MessageCircle size={18} />
                      <span>{post.comments || 0}</span>
                    </button>
                  </div>

                  {/* Comments section */}
                  {expandedCommentsPostId === post.id && (
                    <div style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid var(--border-gray)'
                    }}>
                      {commentsLoading === post.id ? (
                        <p style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>Loading comments...</p>
                      ) : (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                            {(commentsByPostId[post.id] || []).length === 0 ? (
                              <p style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>No comments yet. Be the first!</p>
                            ) : (
                              (commentsByPostId[post.id] || []).map(c => {
                                const createdAt = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
                                const isOwnComment = currentUser && c.userId != null && String(c.userId) === String(currentUser.uid);
                                const isEditing = editingComment?.postId === post.id && editingComment?.commentId === c.id;
                                return (
                                  <div
                                    key={c.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '0.5rem',
                                      padding: '0.5rem 0',
                                      borderBottom: '1px solid var(--border-gray)'
                                    }}
                                  >
                                    <div style={{
                                      width: '28px',
                                      height: '28px',
                                      borderRadius: '50%',
                                      background: 'linear-gradient(135deg, var(--primary-green), var(--accent-orange))',
                                      flexShrink: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}>
                                      {(c.userName || '?').charAt(0)}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dark)' }}>
                                          {c.userName || 'Anonymous'}
                                        </div>
                                        {isOwnComment && !isEditing && (
                                          <div style={{ position: 'relative' }}>
                                            <button
                                              type="button"
                                              onClick={() => setCommentMenuOpen(commentMenuOpen === `${post.id}-${c.id}` ? null : `${post.id}-${c.id}`)}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: '0.25rem',
                                                cursor: 'pointer',
                                                color: 'var(--text-light)',
                                                borderRadius: '4px'
                                              }}
                                              aria-label="Comment options"
                                            >
                                              <MoreVertical size={16} />
                                            </button>
                                            {commentMenuOpen === `${post.id}-${c.id}` && (
                                              <>
                                                <div
                                                  style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                                                  onClick={() => setCommentMenuOpen(null)}
                                                  aria-hidden="true"
                                                />
                                                <div
                                                  style={{
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: '100%',
                                                    marginTop: '2px',
                                                    background: 'var(--bg-white)',
                                                    border: '1px solid var(--border-gray)',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                    zIndex: 11,
                                                    minWidth: '100px'
                                                  }}
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() => startEditComment(post.id, c)}
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: '0.5rem',
                                                      width: '100%',
                                                      padding: '0.5rem 0.75rem',
                                                      border: 'none',
                                                      background: 'none',
                                                      cursor: 'pointer',
                                                      fontSize: '0.875rem',
                                                      color: 'var(--text-dark)',
                                                      textAlign: 'left'
                                                    }}
                                                  >
                                                    <Pencil size={14} /> Edit
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleDeleteComment(post.id, c.id)}
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: '0.5rem',
                                                      width: '100%',
                                                      padding: '0.5rem 0.75rem',
                                                      border: 'none',
                                                      background: 'none',
                                                      cursor: 'pointer',
                                                      fontSize: '0.875rem',
                                                      color: '#DC2626',
                                                      textAlign: 'left',
                                                      borderTop: '1px solid var(--border-gray)'
                                                    }}
                                                  >
                                                    <Trash2 size={14} /> Delete
                                                  </button>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      {isEditing ? (
                                        <div style={{ marginTop: '0.25rem' }}>
                                          <input
                                            type="text"
                                            value={editCommentText}
                                            onChange={(e) => setEditCommentText(e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') handleEditComment(post.id, c.id, editCommentText);
                                              if (e.key === 'Escape') cancelEditComment();
                                            }}
                                            autoFocus
                                            style={{
                                              width: '100%',
                                              padding: '0.5rem 0.75rem',
                                              border: '1px solid var(--border-gray)',
                                              borderRadius: '8px',
                                              fontSize: '0.9rem',
                                              marginBottom: '0.5rem'
                                            }}
                                          />
                                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button
                                              type="button"
                                              className="btn btn-primary"
                                              onClick={() => handleEditComment(post.id, c.id, editCommentText)}
                                              disabled={!editCommentText.trim()}
                                              style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                                            >
                                              Save
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-secondary"
                                              onClick={cancelEditComment}
                                              style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <div style={{ fontSize: '0.9rem', color: 'var(--text-medium)', lineHeight: 1.4 }}>
                                            {c.text}
                                          </div>
                                          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                                            {formatDate(createdAt)}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                          {currentUser ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                              <input
                                type="text"
                                placeholder="Add a comment..."
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && submitComment(post.id)}
                                style={{
                                  flex: 1,
                                  padding: '0.5rem 0.75rem',
                                  border: '1px solid var(--border-gray)',
                                  borderRadius: '8px',
                                  fontSize: '0.9rem'
                                }}
                              />
                              <button
                                className="btn btn-primary"
                                onClick={() => submitComment(post.id)}
                                disabled={!commentText.trim() || commentSubmitLoading}
                                style={{ padding: '0.5rem 1rem' }}
                              >
                                {commentSubmitLoading ? 'Posting...' : 'Post'}
                              </button>
                            </div>
                          ) : (
                            <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>Sign in to comment.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>

      {/* Post Catch Modal */}
      <PostCatchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreatePost}
        user={currentUser}
      />
    </div>
  );
};

export default Community;
